import React, { useState, useEffect, useRef, useCallback } from 'react'
import { ENV } from '../config/env'

const SpriteAnimatedLogo = ({
  width = 40,
  height = 40,
  frameRate = 30,
  autoPlay = false, // false: hover to play, true: always loop
  filename
}) => {
  const [currentFrame, setCurrentFrame] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [shouldStopAtEnd, setShouldStopAtEnd] = useState(false)
  const [justCompletedCycle, setJustCompletedCycle] = useState(false)
  const [metadata, setMetadata] = useState(null)
  const [spriteLoaded, setSpriteLoaded] = useState(false)
  
  const animationRef = useRef(undefined)
  const lastFrameTime = useRef(Date.now())

  const frameInterval = 1000 / frameRate

  // Load sprite metadata
  useEffect(() => {
    fetch(`${filename}-metadata.json`)
      .then(response => response.json())
      .then((data) => {
        setMetadata(data)
      })
      .catch(error => {
        console.error('Failed to load sprite metadata:', error)
      })
  }, [filename])

  // Preload sprite image
  useEffect(() => {
    if (metadata) {
      const img = new Image()
      img.onload = () => {
        setSpriteLoaded(true)
      }
      img.onerror = (e) => {
        console.error('Failed to load sprite image', e)
      }
      img.src = `${ENV.PIC_BASE_URL}${filename}.png`
    }
  }, [metadata, filename])

  const stopAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = undefined
    }
    setIsPlaying(false)
  }, [])

  const startAnimation = useCallback(() => {
    if (isPlaying || !metadata || !spriteLoaded) return
    
    setIsPlaying(true)
    setJustCompletedCycle(false)
    
    const animate = () => {
      const now = Date.now()
      
      if (now - lastFrameTime.current >= frameInterval) {
        setCurrentFrame(prev => {
          const nextFrame = prev >= metadata.totalFrames - 1 ? 0 : prev + 1
          
          // Mark when we complete a cycle (go from last frame to first frame)
          // Only mark cycle completion in hover mode, not in autoPlay mode
          if (!autoPlay && prev === metadata.totalFrames - 1 && nextFrame === 0) {
            setJustCompletedCycle(true)
          }
          
          return nextFrame
        })
        lastFrameTime.current = now
      }
      
      animationRef.current = requestAnimationFrame(animate)
    }
    
    animationRef.current = requestAnimationFrame(animate)
  }, [isPlaying, metadata, spriteLoaded, frameInterval, autoPlay])

  // Auto start animation if autoPlay is enabled
  useEffect(() => {
    if (autoPlay && metadata && spriteLoaded && !isPlaying) {
      startAnimation()
    }
  }, [autoPlay, metadata, spriteLoaded, isPlaying, startAnimation])

  // Handle stopping animation when cycle completes
  useEffect(() => {
    if (shouldStopAtEnd && justCompletedCycle && currentFrame === 0) {
      stopAnimation()
      setJustCompletedCycle(false)
      setShouldStopAtEnd(false)
    }
  }, [shouldStopAtEnd, justCompletedCycle, currentFrame, stopAnimation])

  const handleMouseEnter = useCallback(() => {
    // Only handle mouse events in hover mode, not autoPlay mode
    if (autoPlay) return
    
    if (shouldStopAtEnd) {
      setShouldStopAtEnd(false)
    } else {
      startAnimation()
    }
  }, [autoPlay, shouldStopAtEnd, startAnimation])

  const handleMouseLeave = useCallback(() => {
    // Only handle mouse events in hover mode, not autoPlay mode
    if (autoPlay) return
    
    if (isPlaying) {
      setShouldStopAtEnd(true)
    }
  }, [autoPlay, isPlaying])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  if (!metadata || !spriteLoaded) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}
      >
        <div style={{ fontSize: '0.75rem', color: '#666' }}>
          Loading...
        </div>
      </div>
    )
  }

  const frame = metadata.frames[currentFrame]
  const scaleX = width / metadata.frameWidth
  const scaleY = height / metadata.frameHeight

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width,
        height,
        overflow: 'hidden',
        backgroundImage: `url(${ENV.PIC_BASE_URL}${filename}.png)`,
        backgroundPosition: `-${frame.x * scaleX}px -${frame.y * scaleY}px`,
        backgroundSize: `${metadata.spriteWidth * scaleX}px ${metadata.spriteHeight * scaleY}px`,
        backgroundRepeat: 'no-repeat'
      }}
    />
  )
}

export const SPRITES = {
  MEMU_LOGO: '/static/logo/memu-sprite',
  ANIM_CLIMB_WALL: '/static/anim/climb-wall-sprite',
  ANIM_HEAD_AND_HANDS: '/static/anim/head-and-hands-sprite',
  ANIM_PROBE_RIGHT: '/static/anim/probe-right-sprite',
  ANIM_PROBE_TOP: '/static/anim/probe-top-sprite',
  ANIM_TAIL_WAVE: '/static/anim/tail-wave-sprite',
  ANIM_WALK: '/static/anim/walk-sprite',
}

export default SpriteAnimatedLogo